/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { MarketModifier } from '@/types/dynamicEvents';
import type { TycoonGameState } from '@/types/tycoon';

export interface MarketConditions {
  // Production & Costs
  productionCostMultiplier: number;
  productionCapacityMultiplier: number;
  regulatoryCosts: number;
  
  // Sales & Marketing
  salesMultiplier: number;
  profitMarginMultiplier: number;
  globalPhonePriceMultiplier: number;
  brandPremium: number;
  
  // Research & Innovation
  researchEfficiency: number;
  innovationRateMultiplier: number;
  techScore: number;
  
  // Market Dynamics
  competitiveIntensity: number;
  consumerInterest: number;
  consumerSatisfaction: number;
  marketGrowthRate: number;
  
  // Demand & Features
  touchscreenDemand: number;
  softwareImportance: number;
  qualityScore: number;
  
  // Business Metrics
  monthlyRevenue: number;
  manufacturerOptions: number;
}

export class MarketModifierService {
  private baseConditions: MarketConditions;
  
  constructor() {
    // Initialize with neutral/baseline market conditions
    this.baseConditions = {
      // Production & Costs (1.0 = normal)
      productionCostMultiplier: 1.0,
      productionCapacityMultiplier: 1.0,
      regulatoryCosts: 0,
      
      // Sales & Marketing (1.0 = normal)
      salesMultiplier: 1.0,
      profitMarginMultiplier: 1.0,
      globalPhonePriceMultiplier: 1.0,
      brandPremium: 0,
      
      // Research & Innovation (1.0 = normal)
      researchEfficiency: 1.0,
      innovationRateMultiplier: 1.0,
      techScore: 0,
      
      // Market Dynamics (1.0 = normal)
      competitiveIntensity: 1.0,
      consumerInterest: 1.0,
      consumerSatisfaction: 0,
      marketGrowthRate: 1.0,
      
      // Demand & Features (1.0 = normal)
      touchscreenDemand: 1.0,
      softwareImportance: 0,
      qualityScore: 0,
      
      // Business Metrics
      monthlyRevenue: 0,
      manufacturerOptions: 0
    };
  }

  // Apply modifiers to get current market conditions
  applyModifiers(
    activeModifiers: Map<string, MarketModifier & { appliedAt: Date; expiresAt?: Date }>,
    region?: string
  ): MarketConditions {
    const conditions = { ...this.baseConditions };
    
    for (const [id, modifier] of activeModifiers) {
      // Skip if region-specific and doesn't match
      if (modifier.region && region && modifier.region !== region) {
        continue;
      }
      
      this.applyModifier(conditions, modifier);
    }
    
    return conditions;
  }

  private applyModifier(conditions: MarketConditions, modifier: MarketModifier): void {
    const field = modifier.field as keyof MarketConditions;
    
    if (!(field in conditions)) {
      console.warn(`Unknown market condition field: ${modifier.field}`);
      return;
    }
    
    switch (modifier.type) {
      case 'multiplier':
        conditions[field] = (conditions[field] as number) * modifier.value;
        break;
        
      case 'additive':
        conditions[field] = (conditions[field] as number) + modifier.value;
        break;
        
      case 'override':
        conditions[field] = modifier.value as any;
        break;
        
      default:
        console.warn(`Unknown modifier type: ${modifier.type}`);
    }
  }

  // Apply market conditions to game calculations
  applyToProductionCosts(baseCost: number, conditions: MarketConditions): number {
    let modifiedCost = baseCost * conditions.productionCostMultiplier;
    modifiedCost += conditions.regulatoryCosts;
    return Math.max(0, modifiedCost);
  }

  applyToSales(baseSales: number, conditions: MarketConditions): number {
    let modifiedSales = baseSales * conditions.salesMultiplier;
    modifiedSales *= conditions.consumerInterest;
    modifiedSales *= (1 + conditions.consumerSatisfaction);
    return Math.max(0, modifiedSales);
  }

  applyToPricing(basePrice: number, conditions: MarketConditions): number {
    let modifiedPrice = basePrice * conditions.globalPhonePriceMultiplier;
    modifiedPrice += conditions.brandPremium * basePrice;
    return Math.max(0, modifiedPrice);
  }

  applyToResearch(baseEfficiency: number, conditions: MarketConditions): number {
    return Math.max(0, baseEfficiency * conditions.researchEfficiency);
  }

  applyToProduction(baseCapacity: number, conditions: MarketConditions): number {
    return Math.max(0, baseCapacity * conditions.productionCapacityMultiplier);
  }

  applyToProfitMargins(baseMargin: number, conditions: MarketConditions): number {
    return Math.max(0, baseMargin * conditions.profitMarginMultiplier);
  }

  // Get market pressure indicators for UI display
  getMarketPressures(conditions: MarketConditions): {
    level: 'low' | 'medium' | 'high' | 'extreme';
    factors: string[];
    opportunities: string[];
    threats: string[];
  } {
    const factors: string[] = [];
    const opportunities: string[] = [];
    const threats: string[] = [];
    
    // Analyze cost pressures
    if (conditions.productionCostMultiplier > 1.1) {
      threats.push(`Production costs elevated (+${((conditions.productionCostMultiplier - 1) * 100).toFixed(0)}%)`);
      factors.push('High production costs');
    } else if (conditions.productionCostMultiplier < 0.9) {
      opportunities.push(`Lower production costs (-${((1 - conditions.productionCostMultiplier) * 100).toFixed(0)}%)`);
    }
    
    // Analyze demand conditions
    if (conditions.consumerInterest > 1.1) {
      opportunities.push(`High consumer demand (+${((conditions.consumerInterest - 1) * 100).toFixed(0)}%)`);
    } else if (conditions.consumerInterest < 0.9) {
      threats.push(`Weak consumer demand (-${((1 - conditions.consumerInterest) * 100).toFixed(0)}%)`);
      factors.push('Low market interest');
    }
    
    // Analyze competitive environment
    if (conditions.competitiveIntensity > 1.2) {
      threats.push('Intense competition');
      factors.push('High competitive pressure');
    } else if (conditions.competitiveIntensity < 0.8) {
      opportunities.push('Reduced competition');
    }
    
    // Analyze innovation environment
    if (conditions.researchEfficiency > 1.1) {
      opportunities.push(`Research boost (+${((conditions.researchEfficiency - 1) * 100).toFixed(0)}%)`);
    } else if (conditions.researchEfficiency < 0.9) {
      threats.push(`Slower innovation (-${((1 - conditions.researchEfficiency) * 100).toFixed(0)}%)`);
    }
    
    // Determine overall pressure level
    const threatCount = threats.length;
    const opportunityCount = opportunities.length;
    const netPressure = threatCount - opportunityCount;
    
    let level: 'low' | 'medium' | 'high' | 'extreme';
    if (netPressure >= 3) level = 'extreme';
    else if (netPressure >= 1) level = 'high';
    else if (netPressure >= -1) level = 'medium';
    else level = 'low';
    
    return { level, factors, opportunities, threats };
  }

  // Calculate market attractiveness score for strategic planning
  calculateMarketAttractiveness(conditions: MarketConditions): {
    score: number; // 0-100
    factors: { name: string; impact: number; positive: boolean }[];
  } {
    const factors: { name: string; impact: number; positive: boolean }[] = [];
    let score = 50; // Base neutral score
    
    // Market growth (±20 points)
    const growthImpact = (conditions.marketGrowthRate - 1) * 20;
    score += growthImpact;
    if (Math.abs(growthImpact) > 2) {
      factors.push({
        name: 'Market Growth Rate',
        impact: Math.abs(growthImpact),
        positive: growthImpact > 0
      });
    }
    
    // Consumer interest (±15 points)
    const interestImpact = (conditions.consumerInterest - 1) * 15;
    score += interestImpact;
    if (Math.abs(interestImpact) > 2) {
      factors.push({
        name: 'Consumer Interest',
        impact: Math.abs(interestImpact),
        positive: interestImpact > 0
      });
    }
    
    // Competition level (±10 points, inverse)
    const competitionImpact = (1 - conditions.competitiveIntensity) * 10;
    score += competitionImpact;
    if (Math.abs(competitionImpact) > 2) {
      factors.push({
        name: 'Competitive Intensity',
        impact: Math.abs(competitionImpact),
        positive: competitionImpact > 0
      });
    }
    
    // Production efficiency (±10 points)
    const productionImpact = (conditions.productionCapacityMultiplier - 1) * 10;
    score += productionImpact;
    if (Math.abs(productionImpact) > 1) {
      factors.push({
        name: 'Production Efficiency',
        impact: Math.abs(productionImpact),
        positive: productionImpact > 0
      });
    }
    
    // Research environment (±10 points)
    const researchImpact = (conditions.researchEfficiency - 1) * 10;
    score += researchImpact;
    if (Math.abs(researchImpact) > 1) {
      factors.push({
        name: 'Innovation Environment',
        impact: Math.abs(researchImpact),
        positive: researchImpact > 0
      });
    }
    
    // Cost pressures (±10 points, inverse)
    const costImpact = (1 - conditions.productionCostMultiplier) * 10;
    score += costImpact;
    if (Math.abs(costImpact) > 1) {
      factors.push({
        name: 'Cost Environment',
        impact: Math.abs(costImpact),
        positive: costImpact > 0
      });
    }
    
    return {
      score: Math.max(0, Math.min(100, score)),
      factors: factors.sort((a, b) => b.impact - a.impact)
    };
  }

  // Restore market conditions from saved game
  restoreConditions(conditions: Partial<MarketConditions>): void {
    // console.log('MarketModifierService: Restoring market conditions', conditions);
    
    // Apply restored conditions to base conditions
    Object.entries(conditions).forEach(([key, value]) => {
      if (key in this.baseConditions && typeof value === 'number') {
        (this.baseConditions as any)[key] = value;
      }
    });
    
    // console.log('MarketModifierService: Restored market conditions');
  }

  // Reset the service to initial state
  public reset(): void {
    // console.log('MarketModifierService: Resetting to initial state');
    
    // Reset base conditions to default values
    this.baseConditions = {
      // Production & Costs (1.0 = normal)
      productionCostMultiplier: 1.0,
      productionCapacityMultiplier: 1.0,
      regulatoryCosts: 0,
      
      // Sales & Marketing (1.0 = normal)
      salesMultiplier: 1.0,
      profitMarginMultiplier: 1.0,
      globalPhonePriceMultiplier: 1.0,
      brandPremium: 0,
      
      // Research & Innovation (1.0 = normal)
      researchEfficiency: 1.0,
      innovationRateMultiplier: 1.0,
      techScore: 0,
      
      // Market Dynamics (1.0 = normal)
      competitiveIntensity: 1.0,
      consumerInterest: 1.0,
      consumerSatisfaction: 0,
      marketGrowthRate: 1.0,
      
      // Demand & Features (1.0 = normal)
      touchscreenDemand: 1.0,
      softwareImportance: 0,
      qualityScore: 0,
      
      // Business Metrics
      monthlyRevenue: 0,
      manufacturerOptions: 0
    };
    
    // console.log('MarketModifierService: Reset complete');
  }
}

// Singleton instance
export const marketModifierService = new MarketModifierService();